import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useBalanceConfig } from '@features/balanceLedger'
import { useIsFetchingNewParams } from '@features/expertReports'

import { defaultCacheTime, defaultStaleTime } from '../../../config/queryClient'
import { LedgerEntryIdPrefix } from '../enums/ledgerEntryIdPrefix'
import type {
  CreditorBalancePagination,
  CreditorBalanceStatementContact,
  FetchCreditorBalanceStatementParams,
} from '../query-api'
import type { LedgerEntry } from '../types/LedgerEntry'
import { minorToMajor } from '../utils/moneyUtils'

interface UseFetchCreditorBalanceStatementResult {
  entries: LedgerEntry[]
  totalBalance: number
  openingBalance: number | undefined
  contact?: CreditorBalanceStatementContact
  pagination?: CreditorBalancePagination
  isLoading: boolean
  // True when keepPreviousData is serving stale rows because a non-page param (period/search/contact) changed.
  // Page-only changes keep this false so pagination stays smooth. See useIsFetchingNewParams in @features/expertReports.
  isFetchingNewParams: boolean
  isError: boolean
}

export const useFetchCreditorBalanceStatement = ({
  organizationId,
  contactId,
  period,
  search,
  page,
  pageSize,
  sortBy,
  sortDirection,
}: FetchCreditorBalanceStatementParams): UseFetchCreditorBalanceStatementResult => {
  const config = useBalanceConfig()
  const identityParams = { organizationId, contactId, period, search, sortBy, sortDirection }
  const queryParams = { ...identityParams, page, pageSize }

  const { data, isLoading, isIdle, isError, isPreviousData } = useQuery(
    [config.queryStatementKey, queryParams],
    () => config.fetchStatement(queryParams),
    {
      cacheTime: defaultCacheTime,
      enabled: !!organizationId && !!contactId && !!period,
      staleTime: defaultStaleTime,
      keepPreviousData: true,
    },
  )

  const entries = useMemo(
    () =>
      (data?.data.entries ?? []).map((entry, index) =>
        config.normalizeStatementEntry(entry, index, LedgerEntryIdPrefix.Statement),
      ),
    [config, data?.data.entries],
  )

  // Folded into the shared helper's result rather than left as a separate AND: an errored fetch has settled
  // (successfully or not), so it should never itself read as "fetching new params".
  const isFetchingNewParams = useIsFetchingNewParams(identityParams, isPreviousData) && !isError

  return {
    entries,
    totalBalance: data ? minorToMajor(data.data.closingBalance.value) : 0,
    openingBalance: data ? minorToMajor(data.data.openingBalance.value) : undefined,
    contact: data?.data.contact,
    pagination: data?.data.pagination,
    isLoading: isIdle || isLoading,
    isFetchingNewParams,
    isError,
  }
}
